/**
 * 供应商信息服务（渲染，校验）
 */
import { factoryAreaEnum } from "@/service/supplier-meta/enums/factoryAreaEnum";
import { factoryCategoryEnum } from "@/service/supplier-meta/enums/factoryCategoryEnum";
import { factorySkillEnum } from "@/service/supplier-meta/enums/factorySkillEnum";
import { SelectOption } from "@/service/common/entity/SelectOption";
// import { Category } from "@/service/product/entity/Category";
import { customSupplierTypeEnum } from "@/service/supplier-meta/enums/customSupplierTypeEnum";
import { supplyClassificationEnum } from "@/service/supplier-meta/enums/supplyClassificationEnum";
import { supplyTypeEnum } from "@/service/supplier-meta/enums/supplyTypeEnum";
import { getNewProductCategory } from "../api";
import { NewProductCategoryOption } from "../entity/NewProductCategoryOption";
import Vue from "vue";
/** 服装品类ID */
export const clothingCategoryId = "3";
/**
 * 公共工厂区域获取工厂类别选项
 * @param factArea 工厂区域
 * @param factoryCategoryOptions 工厂类别选项（全量）
 * @returns
 */
export function getFactoryCategoryOptions(
  factArea: number | null,
  factoryCategoryOptions: SelectOption[]
): SelectOption[] {
  if (factArea === factoryAreaEnum.FOB) {
    return factoryCategoryOptions.filter((opt) =>
      [factoryCategoryEnum.A类, factoryCategoryEnum.B类].includes(+opt.value)
    );
  } else {
    return factoryCategoryOptions;
  }
}
/**
 * 通过key获取选项
 * @param keys
 * @param options
 * @returns
 */
export function getOptionsByKeys(
  keys: number[] | string[],
  options: SelectOption[]
): SelectOption[] {
  const opts: SelectOption[] = [];
  keys.forEach((k: number | string) => {
    const matchedOption = options.find((opt) => opt.value === k);
    if (matchedOption) {
      opts.push(matchedOption);
    }
  });
  return opts;
}
/**
 * 根据“工厂区域”，“工厂类别”获取工厂技能选项
 * @param factoryArea 工厂区域
 * @param factoryCategory 工厂类别
 * @param factorySkillOptions 工厂技能选项（全量）
 * @returns
 */
export function getFactorySkillOptions(
  factoryArea: number | null,
  factoryCategory: number | null,
  factorySkillOptions: SelectOption[]
): SelectOption[] {
  if (factoryArea === factoryAreaEnum.FOB) {
    switch (factoryCategory) {
      case factoryCategoryEnum.A类:
        return getOptionsByKeys(
          [factorySkillEnum.版房, factorySkillEnum.加工, factorySkillEnum.测试],
          factorySkillOptions
        );
      case factoryCategoryEnum.B类:
        return getOptionsByKeys(
          [factorySkillEnum.版房, factorySkillEnum.加工],
          factorySkillOptions
        );
    }
  } else {
    switch (factoryCategory) {
      case factoryCategoryEnum.A类:
        return getOptionsByKeys(
          [
            factorySkillEnum.版房,
            factorySkillEnum.裁床,
            factorySkillEnum.车缝,
            factorySkillEnum.尾部,
            factorySkillEnum.测试,
          ],
          factorySkillOptions
        );
      case factoryCategoryEnum.B类:
        return getOptionsByKeys(
          [
            factorySkillEnum.版房,
            factorySkillEnum.裁床,
            factorySkillEnum.车缝,
            factorySkillEnum.尾部,
          ],
          factorySkillOptions
        );
      case factoryCategoryEnum.C类:
        return getOptionsByKeys(
          [factorySkillEnum.版房, factorySkillEnum.车缝, factorySkillEnum.尾部],
          factorySkillOptions
        );
      case factoryCategoryEnum.D类:
        return getOptionsByKeys(
          [factorySkillEnum.版房, factorySkillEnum.车缝],
          factorySkillOptions
        );
      case factoryCategoryEnum.E类:
        return getOptionsByKeys([factorySkillEnum.裁床], factorySkillOptions);
    }
  }
  return [];
}
/**
 * 获取工厂技能默认值
 * @param factoryArea 工厂区域
 * @param factoryCategory 工厂类别
 * @returns
 */
export function getFactorySkillDefaultValue(
  factoryArea: number | null,
  factoryCategory: number | null
): number[] {
  if (factoryArea === factoryAreaEnum.FOB) {
    switch (factoryCategory) {
      case factoryCategoryEnum.A类:
        return [factorySkillEnum.加工, factorySkillEnum.测试];
      case factoryCategoryEnum.B类:
        return [factorySkillEnum.加工];
    }
  } else {
    switch (factoryCategory) {
      case factoryCategoryEnum.A类:
        return [
          factorySkillEnum.裁床,
          factorySkillEnum.车缝,
          factorySkillEnum.尾部,
          factorySkillEnum.测试,
        ];
      case factoryCategoryEnum.B类:
        return [
          factorySkillEnum.裁床,
          factorySkillEnum.车缝,
          factorySkillEnum.尾部,
        ];
      case factoryCategoryEnum.C类:
        return [factorySkillEnum.车缝, factorySkillEnum.尾部];
      case factoryCategoryEnum.D类:
        return [factorySkillEnum.车缝];
      case factoryCategoryEnum.E类:
        return [factorySkillEnum.裁床];
    }
  }
  return [];
}
/**
 * 是否工厂技能可勾选
 * @param val
 * @returns
 */
export function isFactorySkillCheckable(val: number) {
  return val === factorySkillEnum.版房;
}
/**
 * 是否是服装品类
 * @param category
 * @returns
 */
export function isClothingCategory(categoryId: string): boolean {
  return +categoryId === +clothingCategoryId;
}
/**
 * 获取供应商类型（用于字段渲染和校验）
 * @param isClothingCategory 是否服装分类
 * @param supplyClassification 供货分类
 * @param supplyType 供应链类型
 * @returns
 */
export function getCustomSupplierType(
  isClothingCategory: boolean,
  supplyClassification: number | null | undefined,
  supplyType: number
) {
  if (isClothingCategory) {
    // 服装
    if (
      supplyClassification === supplyClassificationEnum.成衣 &&
      [
        supplyTypeEnum.FGP线上,
        supplyTypeEnum.FGP线下,
        supplyTypeEnum.VMI,
      ].includes(supplyType)
    ) {
      return customSupplierTypeEnum["服装_成衣_FGP/VMI"];
    } else if (
      supplyClassification === supplyClassificationEnum.成衣 &&
      [supplyTypeEnum.OEM].includes(supplyType)
    ) {
      return customSupplierTypeEnum.服装_成衣_OEM;
    } else if (
      supplyClassification === supplyClassificationEnum.成衣 &&
      [supplyTypeEnum.ODM].includes(supplyType)
    ) {
      return customSupplierTypeEnum.服装_成衣_ODM;
    } else if (
      supplyClassification === supplyClassificationEnum.成衣 &&
      [supplyTypeEnum.IPD].includes(supplyType)
    ) {
      return customSupplierTypeEnum.服装_成衣_IPD;
    } else if (supplyClassification !== supplyClassificationEnum.成衣) {
      return customSupplierTypeEnum.服装_面辅料;
    } else {
      // console.log("supplyClassification", supplyClassification);
      // console.log("supplyType", supplyType);
    }
  } else {
    // 非服装
    if (
      [
        supplyTypeEnum.FGP线上,
        supplyTypeEnum.FGP线下,
        supplyTypeEnum.VMI,
      ].includes(supplyType)
    ) {
      return customSupplierTypeEnum["非服装_FGP/VMI"];
    } else if (
      [supplyTypeEnum.IPD, supplyTypeEnum.OEM, supplyTypeEnum.ODM].includes(
        supplyType
      )
    ) {
      return customSupplierTypeEnum["非服装_IPD/OEM/ODM"];
    }
  }
}
/**
 * 获取供应商服务列表
 * @param isClothingCategory 是否服装品类
 * @param supplyClassification 供货分类
 * @param supplyType 供应链类型
 */
export function getSupplierServices(
  isClothingCategory: boolean,
  supplyClassification: number | null | undefined,
  supplyType: number
) {
  const customSupplierType = getCustomSupplierType(
    isClothingCategory,
    supplyClassification,
    supplyType
  );
  switch (customSupplierType) {
    case customSupplierTypeEnum["非服装_FGP/VMI"]:
      return [
        "账期",
        "分拣",
        "包装",
        "产品退换保修",
        "提前开票",
        "送货贴条形码",
        "送货到指定地点",
        "使用供应商系统",
        "自主打印洗水唛服务",
      ];
    case customSupplierTypeEnum["非服装_IPD/OEM/ODM"]:
      return [
        "账期",
        "分拣",
        "包装",
        "产品退换保修",
        "提前开票",
        "送货贴条形码",
        "送货到指定地点",
        "使用供应商系统",
        "自主打印洗水唛服务",
      ];
    case customSupplierTypeEnum["服装_成衣_FGP/VMI"]:
      return [
        "账期",
        "分拣",
        "包装",
        "产品退换保修",
        "提前开票",
        "送货贴条形码",
        "送货到指定地点",
        "使用供应商系统",
        "自主打印洗水唛服务",
        "提供产品尺码表",
        "免费打版",
        "提供产品度量方法",
        "免费提供样衣",
        "提供产品上架自拍图",
      ];
    case customSupplierTypeEnum.服装_成衣_OEM:
      return [
        "账期",
        "分拣",
        "包装",
        "产品退换保修",
        "提前开票",
        "送货贴条形码",
        "送货到指定地点",
        "使用供应商系统",
        "自主打印洗水唛服务",
        "提供产品尺码表",
        "免费打版",
        "提供产品度量方法",
        "免费提供样衣",
      ];
    case customSupplierTypeEnum.服装_成衣_ODM:
      return [
        "账期",
        "分拣",
        "包装",
        "产品退换保修",
        "提前开票",
        "送货贴条形码",
        "送货到指定地点",
        "使用供应商系统",
        "自主打印洗水唛服务",
        "提供产品尺码表",
        "免费打版",
        "提供产品度量方法",
        "免费提供样衣",
        "提供产品上架自拍图",
      ];
    case customSupplierTypeEnum.服装_成衣_IPD:
      return [
        "账期",
        "分拣",
        "包装",
        "产品退换保修",
        "提前开票",
        "送货贴条形码",
        "送货到指定地点",
        "使用供应商系统",
        "自主打印洗水唛服务",
        "免费打版",
        "免费提供样衣",
        "免费送货服务",
      ];
    case customSupplierTypeEnum.服装_面辅料:
      return [
        "账期",
        "产品退换保修",
        "提前开票",
        "送货到指定地点",
        "使用供应商系统",
        "免费打版",
        "阶梯价",
        "免费送货服务",
        "提供面料自检服务",
        "生产事故责任承担",
      ];
  }
  return [];
}

/**
 * 根据id路径补全品类数据，用于按需加载的品类树的已选值的回显示
 * @param id
 */
export async function fillCategoryOptions(
  idPaths: string[],
  categoryData: NewProductCategoryOption[]
) {
  let curLevelCategoryOptions = categoryData; //当前层级品类数据
  if (idPaths.length === 1) {
    return;
  }
  const wouldLoadChildrenIds = idPaths.slice(0, idPaths.length - 1);
  for (const i of wouldLoadChildrenIds) {
    const targetCategory = curLevelCategoryOptions.find(
      (category) => category.id === i
    );
    if (!targetCategory) {
      break;
    } else if (
      Array.isArray(targetCategory.children) &&
      targetCategory.children.length
    ) {
      continue;
    }
    const categoryListRes = await getNewProductCategory(i + "");
    if (categoryListRes.is_success && Array.isArray(categoryListRes.result)) {
      Vue.set(targetCategory, "children", categoryListRes.result);
      curLevelCategoryOptions = targetCategory.children || [];
    } else {
      break;
    }
  }
}
